import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { debounce } from 'lodash';

import * as ExperienceActions from 'store/templated-experience/templated-experience.action';
import { useThunkDispatch as useDispatch } from 'hooks/reduxHooks';
import useActions from 'hooks/useActions';
import { ERROR_TYPES, TExperienceCanvasRouteParams } from 'pages/ExperienceCanvas/types';
import { JourneyState } from 'store/journey/journey.type';

export type ExperienceNameValidation = {
  experienceName: string;
  experienceNameError: string;
  onChangeName: (name: string) => void;
  isCheckingUniqueness: boolean;
};

/**
 * Owns the experience-name empty/uniqueness validation for the v2 header. Re-implemented cleanly
 * here rather than imported from v1's `PrimaryHeader`: an empty trimmed name errors immediately, a
 * name colliding with another instance errors after a debounced backend lookup, and any valid name
 * is written back to the journey store. The resulting `experienceNameError` feeds the launch/save
 * guards in {@link useJourneyActions}.
 */
export default function useExperienceNameValidation(journeyInstance: JourneyState): ExperienceNameValidation {
  const dispatch = useDispatch();
  const teActions = useActions(ExperienceActions);
  const { id: journeyId } = useRouteMatch<TExperienceCanvasRouteParams>().params;

  const [experienceName, setExperienceName] = useState(journeyInstance?.name || '');
  const [experienceNameError, setExperienceNameError] = useState('');
  const [isCheckingUniqueness, setIsCheckingUniqueness] = useState(false);
  const validationRequestIdRef = useRef(0);

  const checkExperienceNameUniqueness = useCallback(
    async (name: string) => {
      const requestId = ++validationRequestIdRef.current;
      setIsCheckingUniqueness(true);
      const trimmed = name.trim();

      if (!trimmed) {
        if (requestId !== validationRequestIdRef.current) return;
        setExperienceNameError('Experience name cannot be empty');
        setIsCheckingUniqueness(false);
        return;
      }

      const { data: matchingInstances } = await teActions.fetchInstances({ search: trimmed, searchExact: true });

      if (requestId !== validationRequestIdRef.current) return;

      if (!matchingInstances || matchingInstances.error) {
        dispatch({
          type: 'SHOW_SNACKBAR',
          payload: {
            content: 'Something went wrong while checking experience name uniqueness',
            type: ERROR_TYPES.ERROR,
          },
        });
        setIsCheckingUniqueness(false);
        return;
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const isDuplicate = matchingInstances.some((instance: any) => instance.id !== journeyId);
      if (isDuplicate) {
        setExperienceNameError('Experience name is not unique');
        dispatch({
          type: 'SHOW_SNACKBAR',
          payload: { content: 'Experience name is not unique', type: ERROR_TYPES.ERROR },
        });
      } else {
        setExperienceNameError('');
      }

      dispatch({ type: 'UPDATE_JOURNEY', payload: { name: trimmed } });
      setIsCheckingUniqueness(false);
    },
    [dispatch, journeyId, teActions],
  );

  // Re-validate on load: returning to the page after editing a step resets the local error, so a
  // name that duplicates another journey must be re-flagged.
  useEffect(() => {
    checkExperienceNameUniqueness(journeyInstance?.name ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const debouncedCheck = useMemo(
    () => debounce((name: string) => checkExperienceNameUniqueness(name), 500),
    [checkExperienceNameUniqueness],
  );

  const onChangeName = useCallback(
    (name: string) => {
      setExperienceName(name);

      if (!name.trim()) {
        ++validationRequestIdRef.current;
        debouncedCheck.cancel();
        setExperienceNameError('Experience name cannot be empty');
        setIsCheckingUniqueness(false);
        return;
      }

      debouncedCheck(name);
    },
    [debouncedCheck],
  );

  useEffect(() => {
    return () => {
      debouncedCheck.cancel();
    };
  }, [debouncedCheck]);

  return { experienceName, experienceNameError, onChangeName, isCheckingUniqueness };
}
